<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import TemplateAlerts from './TemplateAlerts.vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useModulePermissions } from 'dashboard/composables/useModulePermissions';
import { useAlert } from 'dashboard/composables';
import { useNow } from '@vueuse/core';
import { generateRelativeTime } from 'shared/helpers/DateHelper';
import InboxesAPI from 'dashboard/api/inboxes';
import {
  estimatedTemplatePrice,
  formatEstimatedPrice,
  WHATSAPP_BRL_PRICES,
} from 'dashboard/helper/whatsappPricing';
import CreateTemplateDialog from './CreateTemplateDialog.vue';
import {
  CATEGORY_REASONS,
  FILTER_GROUPS,
  canEditTemplate,
  isInReview,
  pauseLabel,
  qualityScore,
  reasonKey,
  reasonLabel,
  statusGroup,
  statusStyle,
  statusLabel,
} from './templateStatus';

const props = defineProps({
  inbox: { type: Object, required: true },
});
const REFRESH_INTERVAL_MS = 60 * 1000;
const REFRESH_MAX_MS = 30 * 60 * 1000;
const APPEAL_URL = 'https://business.facebook.com/business-support-home';
const RELATIVE_TIME_TICK_MS = 30 * 1000;
const RELATIVE_UNITS = [
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
];

const QUALITY_STYLES = {
  GREEN: 'bg-n-teal-9',
  YELLOW: 'bg-n-amber-9',
  RED: 'bg-n-ruby-9',
};

const { t, locale } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { moduleCan } = useModulePermissions();
const canCreate = computed(
  () => isAdmin.value || moduleCan('create', 'disparador')
);

const templates = ref([]);
const lastUpdated = ref(null);
const searchQuery = ref('');
const isFetching = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const showCreateDialog = ref(false);
const dialogMode = ref('create');
const dialogTemplate = ref(null);
const activeFilter = ref('ALL');
let refreshTimer = null;
let refreshStartedAt = 0;
const templateToDelete = ref(null);
const isDeleting = ref(false);
const monthSpend = ref(null);

const now = useNow({ interval: RELATIVE_TIME_TICK_MS });
const lastUpdatedAgo = computed(() => {
  const elapsed =
    (now.value - new Date(lastUpdated.value || now.value).getTime()) / 1000;
  const { unit, seconds } =
    RELATIVE_UNITS.find(item => elapsed >= item.seconds) ??
    RELATIVE_UNITS.at(-1);
  return generateRelativeTime(
    -Math.floor(elapsed / seconds),
    unit,
    locale.value
  );
});

const filterCounts = computed(() => {
  const counts = { ALL: templates.value.length };
  FILTER_GROUPS.forEach(group => {
    counts[group] = templates.value.filter(
      template => statusGroup(template.status) === group
    ).length;
  });
  return counts;
});

const filteredTemplates = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return templates.value.filter(
    template =>
      (activeFilter.value === 'ALL' ||
        statusGroup(template.status) === activeFilter.value) &&
      (!query || template.name?.toLowerCase().includes(query))
  );
});

const hasTemplateInReview = computed(() => templates.value.some(isInReview));
const existingNames = computed(() => templates.value.map(item => item.name));

const KNOWN_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION', 'SERVICE'];

const categoryLabel = category => {
  const key = category?.toUpperCase();
  return KNOWN_CATEGORIES.includes(key)
    ? t(`WHATSAPP_TEMPLATES.MANAGER.CATEGORY.${key}`)
    : category;
};

const qualityTitle = template =>
  t('WHATSAPP_TEMPLATES.MANAGER.QUALITY_TITLE', {
    quality: t(`WHATSAPP_TEMPLATES.MANAGER.QUALITY.${qualityScore(template)}`),
  });

const priceLabel = category => {
  const price = estimatedTemplatePrice(category);
  return price === null ? '' : formatEstimatedPrice(price);
};

const fetchTemplates = async () => {
  isFetching.value = true;
  try {
    const { data } = await InboxesAPI.getWhatsAppTemplates(props.inbox.id);
    templates.value = Array.isArray(data.templates) ? data.templates : [];
    lastUpdated.value = data.last_updated;
  } catch {
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.LIST.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const fetchMonthSpend = async () => {
  try {
    const { data } = await InboxesAPI.getWhatsAppPricingAnalytics(
      props.inbox.id
    );
    const points = (data.data || []).flatMap(entry => entry.data_points || []);
    if (!points.length) {
      monthSpend.value = { total: 0, byCategory: {} };
      return;
    }
    const byCategory = {};
    let total = 0;
    points.forEach(point => {
      const category = point.pricing_category || 'OUTROS';
      const cost = Number(point.cost) || 0;
      byCategory[category] = (byCategory[category] || 0) + cost;
      total += cost;
    });
    monthSpend.value = { total, byCategory };
  } catch {
    monthSpend.value = null;
  }
};

const refetchAfterSync = () => {
  let attempts = 0;
  const previous = lastUpdated.value;
  const interval = setInterval(async () => {
    attempts += 1;
    await fetchTemplates();
    if (lastUpdated.value !== previous || attempts >= 3) {
      clearInterval(interval);
      isSyncing.value = false;
    }
  }, 4000);
};

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    refetchAfterSync();
  } catch {
    isSyncing.value = false;
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.LIST.SYNC_ERROR'));
  }
};

const openDialog = (mode, template = null) => {
  dialogMode.value = mode;
  dialogTemplate.value = template;
  showCreateDialog.value = true;
};

const createTemplate = async payload => {
  isSaving.value = true;
  try {
    await InboxesAPI.createWhatsAppTemplate(props.inbox.id, payload);
    showCreateDialog.value = false;
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.CREATE.SUCCESS'));
    setTimeout(fetchTemplates, 4000);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('WHATSAPP_TEMPLATES.MANAGER.CREATE.ERROR');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
};

const updateTemplate = async ({ name, language, payload }) => {
  isSaving.value = true;
  try {
    await InboxesAPI.updateWhatsAppTemplate(
      props.inbox.id,
      name,
      language,
      payload
    );
    showCreateDialog.value = false;
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.CREATE.SUCCESS'));
    setTimeout(fetchTemplates, 4000);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.MANAGER.CREATE.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const deleteTemplate = async () => {
  isDeleting.value = true;
  try {
    await InboxesAPI.deleteWhatsAppTemplate(
      props.inbox.id,
      templateToDelete.value.name
    );
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.DELETE.SUCCESS'));
    templateToDelete.value = null;
    setTimeout(fetchTemplates, 4000);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('WHATSAPP_TEMPLATES.MANAGER.DELETE.ERROR');
    useAlert(message);
  } finally {
    isDeleting.value = false;
  }
};

const stopRefresh = () => {
  clearInterval(refreshTimer);
  refreshTimer = null;
};

watch(hasTemplateInReview, inReview => {
  if (!inReview) {
    stopRefresh();
    return;
  }
  if (refreshTimer) return;
  refreshStartedAt = Date.now();
  refreshTimer = setInterval(() => {
    if (Date.now() - refreshStartedAt >= REFRESH_MAX_MS) {
      stopRefresh();
      return;
    }
    if (!isSyncing.value) syncTemplates();
  }, REFRESH_INTERVAL_MS);
});

onUnmounted(stopRefresh);

onMounted(() => {
  fetchTemplates();
  fetchMonthSpend();
});
</script>

<template>
  <div class="mx-6 flex max-w-5xl flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-lg font-medium">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.DESCRIPTION') }}
        </p>
        <p v-if="lastUpdated" class="text-xs text-n-slate-10">
          {{
            $t('WHATSAPP_TEMPLATES.MANAGER.LIST.SYNCED_AT', {
              time: lastUpdatedAgo,
            })
          }}
        </p>
      </div>
      <div class="flex shrink-0 gap-2">
        <woot-button
          variant="clear"
          icon="arrow-clockwise"
          :is-loading="isSyncing"
          @click="syncTemplates"
        >
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.SYNC') }}
        </woot-button>
        <woot-button v-if="canCreate" icon="add" @click="openDialog('create')">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.NEW_TEMPLATE') }}
        </woot-button>
      </div>
    </div>

    <div class="flex flex-wrap gap-3">
      <div class="flex-1 rounded-lg border border-n-weak p-3">
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.PRICING.TITLE') }}
        </span>
        <div class="mt-1 flex flex-wrap gap-3 text-sm">
          <span
            v-for="(price, category) in WHATSAPP_BRL_PRICES"
            :key="category"
          >
            {{ $t(`WHATSAPP_TEMPLATES.MANAGER.CATEGORY.${category}`) }}:
            <span class="font-medium">{{ formatEstimatedPrice(price) }}</span>
          </span>
        </div>
        <span class="mt-1 block text-xs text-n-slate-10">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.PRICING.DISCLAIMER') }}
        </span>
      </div>
      <div v-if="monthSpend" class="rounded-lg border border-n-weak p-3">
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.PRICING.MONTH_SPEND') }}
        </span>
        <div class="mt-1 text-lg font-semibold">
          {{ formatEstimatedPrice(monthSpend.total) }}
        </div>
        <div class="flex flex-wrap gap-2 text-xs text-n-slate-11">
          <span
            v-for="(cost, category) in monthSpend.byCategory"
            :key="category"
          >
            {{ category }}: {{ formatEstimatedPrice(cost) }}
          </span>
          <span v-if="!Object.keys(monthSpend.byCategory).length">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.PRICING.NO_SPEND') }}
          </span>
        </div>
      </div>
    </div>

    <div class="rounded-lg border border-n-weak p-3 text-xs text-n-slate-11">
      <span class="font-medium text-n-slate-12">
        {{ $t('WHATSAPP_TEMPLATES.MANAGER.HELP.TITLE') }}
      </span>
      <ul class="mt-1 list-disc ps-4">
        <li v-for="line in ['L1', 'L2', 'L3', 'L4']" :key="line">
          {{ $t(`WHATSAPP_TEMPLATES.MANAGER.HELP.${line}`) }}
        </li>
      </ul>
      <a
        :href="APPEAL_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-1 inline-block text-n-brand"
      >
        {{ $t('WHATSAPP_TEMPLATES.MANAGER.HELP.APPEAL') }}
      </a>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <button
        v-for="filter in ['ALL', ...FILTER_GROUPS]"
        :key="filter"
        type="button"
        data-test="status-filter"
        class="rounded-full border px-3 py-1 text-xs font-medium"
        :class="
          activeFilter === filter
            ? 'border-n-brand bg-n-brand/10 text-n-brand'
            : 'border-n-weak text-n-slate-11'
        "
        @click="activeFilter = filter"
      >
        {{ $t(`WHATSAPP_TEMPLATES.MANAGER.FILTERS.${filter}`) }}
        ({{ filterCounts[filter] }})
      </button>
      <span v-if="hasTemplateInReview" class="text-xs text-n-slate-10">
        {{ $t('WHATSAPP_TEMPLATES.MANAGER.AUTO_REFRESH_HINT') }}
      </span>
    </div>

    <input
      v-model="searchQuery"
      type="search"
      class="max-w-xs"
      :placeholder="$t('WHATSAPP_TEMPLATES.MANAGER.LIST.SEARCH_PLACEHOLDER')"
    />

    <div class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak text-left text-xs text-n-slate-11">
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.NAME') }}
            </th>
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.CATEGORY') }}
            </th>
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.LANGUAGE') }}
            </th>
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.PRICE') }}
            </th>
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.STATUS') }}
            </th>
            <th class="p-3">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.TABLE.QUALITY') }}
            </th>
            <th class="p-3" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="template in filteredTemplates"
            :key="`${template.name}-${template.language}`"
            class="border-b border-n-weak last:border-0"
          >
            <td class="p-3 font-medium">{{ template.name }}</td>
            <td class="p-3">
              {{ categoryLabel(template.category) }}
              <TemplateAlerts :template="template" />
            </td>
            <td class="p-3">{{ template.language }}</td>
            <td class="p-3">{{ priceLabel(template.category) }}</td>
            <td class="p-3">
              <span
                class="rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusStyle(template.status)"
              >
                {{ statusLabel(template.status, t) }}
              </span>
              <span
                v-if="pauseLabel(template, t)"
                class="mt-1 block text-xs text-n-amber-11"
              >
                {{ pauseLabel(template, t) }}
              </span>
              <span
                v-if="reasonLabel(template, t)"
                class="mt-1 block text-xs text-n-ruby-9"
              >
                {{ reasonLabel(template, t) }}
              </span>
              <span
                v-if="CATEGORY_REASONS.includes(reasonKey(template))"
                class="mt-1 block text-xs text-n-slate-11"
              >
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.REASON_HINT.CATEGORY') }}
              </span>
              <span
                v-if="template.rejection_info?.recommendation"
                class="mt-1 block text-xs text-n-slate-11"
              >
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.META_RECOMMENDATION') }}:
                <i>{{ template.rejection_info.recommendation }}</i>
              </span>
            </td>
            <td class="p-3">
              <span
                v-if="qualityScore(template)"
                class="inline-block size-3 rounded-full"
                :class="QUALITY_STYLES[qualityScore(template)]"
                :title="qualityTitle(template)"
              />
              <span v-else>—</span>
            </td>
            <td class="p-3 text-right">
              <div class="flex justify-end gap-1">
                <woot-button
                  v-if="canCreate"
                  variant="clear"
                  size="small"
                  data-test="duplicate-template"
                  @click="openDialog('duplicate', template)"
                >
                  {{ $t('WHATSAPP_TEMPLATES.MANAGER.ACTIONS.DUPLICATE') }}
                </woot-button>
                <woot-button
                  v-if="canCreate && canEditTemplate(template)"
                  variant="clear"
                  size="small"
                  data-test="edit-template"
                  :title="
                    template.status === 'APPROVED'
                      ? $t(
                          'WHATSAPP_TEMPLATES.MANAGER.ACTIONS.EDIT_APPROVED_HINT'
                        )
                      : ''
                  "
                  @click="openDialog('edit', template)"
                >
                  {{ $t('WHATSAPP_TEMPLATES.MANAGER.ACTIONS.EDIT') }}
                </woot-button>
                <woot-button
                  v-if="isAdmin"
                  variant="clear"
                  color-scheme="alert"
                  icon="delete"
                  size="small"
                  :title="$t('WHATSAPP_TEMPLATES.MANAGER.ACTIONS.DELETE')"
                  @click="templateToDelete = template"
                />
              </div>
            </td>
          </tr>
          <tr v-if="!filteredTemplates.length && !isFetching">
            <td colspan="7" class="p-6 text-center text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIST.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CreateTemplateDialog
      :show="showCreateDialog"
      :is-saving="isSaving"
      :inbox-id="inbox.id"
      :mode="dialogMode"
      :initial-template="dialogTemplate"
      :existing-names="existingNames"
      @close="showCreateDialog = false"
      @create="createTemplate"
      @update="updateTemplate"
    />

    <woot-delete-modal
      v-if="templateToDelete"
      :show="!!templateToDelete"
      :title="$t('WHATSAPP_TEMPLATES.MANAGER.DELETE.TITLE')"
      :message="
        $t('WHATSAPP_TEMPLATES.MANAGER.DELETE.MESSAGE', {
          name: templateToDelete.name,
        })
      "
      :confirm-text="$t('WHATSAPP_TEMPLATES.MANAGER.DELETE.CONFIRM')"
      :reject-text="$t('WHATSAPP_TEMPLATES.MANAGER.DELETE.CANCEL')"
      :on-close="() => (templateToDelete = null)"
      :on-confirm="deleteTemplate"
    />
  </div>
</template>
